import {
  type MantineFontSizesValues,
  type MantineLineHeightValues,
  type MantineSize,
  type MantineThemeColors,
  em,
} from '@mantine/core'

export const breakpoints: Record<MantineSize, string> = {
  xs: em('480px'),
  sm: em('768px'),
  md: em('1024px'),
  lg: em('1184px'),
  xl: em('1440px'),
}

export const spacing: Record<MantineSize, string> = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
}

export const radius: Record<MantineSize, string> = {
  xs: '6px',
  sm: '8px',
  md: '10px',
  lg: '12px',
  xl: '14px',
}

export const shadows: Record<MantineSize, string> = {
  xs: '0px 7px 20px -2px rgba(0, 0, 0, 0.015)',
  sm: '0px 7px 20px -2px rgba(0, 0, 0, 0.04)',
  md: '0px 7px 20px 0px rgba(0, 0, 0, 0.08)',
  lg: '0px 8px 20px 0px rgba(15, 15, 15, 0.12)',
  xl: '0px 8px 20px 0px rgba(16, 24, 40, 0.20)',
}

export const fontSizes: MantineFontSizesValues = {
  xs: '10px',
  sm: '12px',
  md: '14px',
  lg: '16px',
  xl: '18px',
}

export const lineHeights: MantineLineHeightValues = {
  xs: '1.5',
  sm: '1.5',
  md: '1.5',
  lg: '1.5',
  xl: '1.5',
}

export const headings = {
  h1: '40px',
  h2: '32px',
  h3: '24px',
  h4: '18px',
  h5: '16px',
  h6: '12px',
}

export const lineHeight = '1.5'

export const fontFamily = 'Aeonik Pro, sans-serif'

export const colors: MantineThemeColors = {
  // Main colors
  brand: [
    '#EEFFF6',
    '#D5FEE8',
    '#A1F8CA',
    '#56F09F',
    '#38D98B',
    '#19C276',
    '#17A06D',
    '#147D63',
    '#004737',
    '#032019',
  ],
  cream: [
    '#F7F7F3',
    '#EFECDD',
    '#DFDCCD',
    '#BAB6AA',
    '#A4A092',
    '#979282',
    '#928B77',
    '#7E7865',
    '#716D61',
    '#615C46',
  ],
  gray: [
    '#f8f9fa',
    '#f1f3f5',
    '#e9ecef',
    '#dee2e6',
    '#ced4da',
    '#adb5bd',
    '#85929F',
    '#667085',
    '#2F394C',
    '#101828',
  ],
  red: [
    '#FFEAE9',
    '#FFD4D3',
    '#F7A9A5',
    '#F07A74',
    '#EA524A',
    '#E73930',
    '#E62A22',
    '#CC1D16',
    '#B81612',
    '#A0080B',
  ],
  green: [
    '#EEFFF6',
    '#D5FEE8',
    '#A1F8CA',
    '#56F09F',
    '#38D98B',
    '#19C276',
    '#17A06D',
    '#147D63',
    '#004737',
    '#032019',
  ],
  blue: [
    '#e4f5fd',
    '#bae5f9',
    '#90d5f5',
    '#6ec4ef',
    '#4EA8E6',
    '#349BE3',
    '#2195E2',
    '#0B81CA',
    '#0073B6',
    '#0064A2',
  ],
  orange: [
    '#FFF2E3',
    '#FDE5D1',
    '#F4CBA5',
    '#ECAE77',
    '#E59650',
    '#E18636',
    '#E07F27',
    '#C76B19',
    '#B15F13',
    '#9B5107',
  ],
  purple: [
    '#F1EFFF',
    '#DEDCF5',
    '#BAB6E5',
    '#938DD6',
    '#736AC8',
    '#5F54C1',
    '#5349BE',
    '#453BA8',
    '#3C3597',
    '#312C86',
  ],

  // Other support colors
  dark: [
    '#f8f9fa',
    '#f1f3f5',
    '#e9ecef',
    '#dee2e6',
    '#ced4da',
    '#adb5bd',
    '#85929F',
    '#667085',
    '#2F394C',
    '#101828',
  ],
  pink: [
    '#FFF0F6',
    '#FFDEEB',
    '#FCC2D7',
    '#FAA2C1',
    '#F783AC',
    '#F06595',
    '#E64980',
    '#D6336C',
    '#C2255C',
    '#A61E4D',
  ],
  grape: [
    '#F8F0FC',
    '#F3D9FA',
    '#EEBEFA',
    '#E599F7',
    '#DA77F2',
    '#CC5DE8',
    '#BE4BDB',
    '#AE3EC9',
    '#9C36B5',
    '#862E9C',
  ],
  violet: [
    '#F1EFFF',
    '#DEDCF5',
    '#BAB6E5',
    '#938DD6',
    '#736AC8',
    '#5F54C1',
    '#5349BE',
    '#453BA8',
    '#3C3597',
    '#312C86',
  ],
  indigo: [
    '#EDF2FF',
    '#DBE4FF',
    '#BAC8FF',
    '#91A7FF',
    '#748FFC',
    '#5C7CFA',
    '#4C6EF5',
    '#4263EB',
    '#3B5BDB',
    '#364FC7',
  ],
  cyan: [
    '#E3FAFC',
    '#C5F6FA',
    '#99E9F2',
    '#66D9E8',
    '#3BC9DB',
    '#22B8CF',
    '#15AABF',
    '#1098AD',
    '#0C8599',
    '#0B7285',
  ],
  teal: [
    '#E6FCF5',
    '#C3FAE8',
    '#96F2D7',
    '#63E6BE',
    '#38D9A9',
    '#20C997',
    '#12B886',
    '#0CA678',
    '#099268',
    '#087F5B',
  ],
  lime: [
    '#F4FCE3',
    '#E9FAC8',
    '#D8F5A2',
    '#C0EB75',
    '#A9E34B',
    '#94D82D',
    '#82C91E',
    '#74B816',
    '#66A80F',
    '#5C940D',
  ],
  yellow: [
    '#FFF9DB',
    '#FFF3BF',
    '#FFEC99',
    '#FFE066',
    '#FFD43B',
    '#FCC419',
    '#FAB005',
    '#F59F00',
    '#F08C00',
    '#E67700',
  ],
}
